import {AggregationFunction, EvalType} from "../../pages/alerts/MetricAlert";
import {SmileIcon, Bot} from "lucide-react";
import hdate from "human-date";
import React, {useMemo, useEffect, useState} from "react";
import {ApiServerAlert} from "./utils";
import {DataTable} from "../ui/datatable/datatable";
import {ColumnDef} from "@tanstack/react-table";
import {AlertDestinationType} from "./AlertDestination";
import {Button} from "../ui/button";
import CustomAxios from "../../utility/customAxios";
import {useSearchParams} from "react-router-dom";
import {useFeatureFlagsStore} from "../../store/featureFlagsStore";

export interface AlertFire {
    uuid: string;
    alertUuid: string;
    organizationUuid: string;
    startTime: number;
    endTime: number;
    message: string;
    deepLink: string;
    kubernetesEnvironment: string;
    kubernetesService: string;
    properties: MetricAlertFireProperties;
}

interface MetricAlertFireProperties {
    metricFilters: Map<string, string[]>;
    metricAttributes: Map<string, string>;
    metricName: string;
    aggregation: string;
    datapointBreaching: DataPoint;
}

interface DataPoint {
    time: number;
    value: number;
}

interface ChatHistory {
    id: string;
    title: string;
    createdAt: Date;
}

export function AlertFiringHistoryList(props: {
    alertFires: AlertFire[],
    alert: ApiServerAlert
    alertType: string,
    evaluationType: EvalType,
    evaluationFunction: AggregationFunction
}) {
    const [, setSearchParams] = useSearchParams();
    const [chatHistories, setChatHistories] = useState<ChatHistory[]>([]);
    const [isGuardianAlertDestinationEnabled, setIsGuardianAlertDestinationEnabled] = useState<boolean>(false);
    const featureFlags = useFeatureFlagsStore();
    
    // Check if alert has Guardian destination
    const hasGuardianDestination = props.alert.destinations?.some(
        destination => destination.type === AlertDestinationType.GuardianInvestigation
    );
    
    // Load feature flag
    useEffect(() => {
        featureFlags.featureFlagEnabled("guardian.alert.destination").then((x) => {
            setIsGuardianAlertDestinationEnabled(x);
        });
    }, [featureFlags]);
    
    // Load chat histories when component mounts if feature flag is enabled and alert has Guardian destination
    useEffect(() => {
        if (isGuardianAlertDestinationEnabled && hasGuardianDestination) {
            const loadChatHistories = async () => {
                try {
                    const { data } = await CustomAxios.get('/api/v1/guardian/history');
                    setChatHistories(data.map((history: any) => ({
                        ...history,
                        createdAt: new Date(history.createdAt)
                    })));
                } catch (error) {
                    console.error('Error loading chat histories:', error);
                }
            };
            loadChatHistories();
        }
    }, [isGuardianAlertDestinationEnabled, hasGuardianDestination]);
    
    // Function to check if chat history exists for an alert fire UUID
    const getChatHistoryForAlertFire = (alertFireUuid: string): ChatHistory | undefined => {
        return chatHistories.find(history => history.id === alertFireUuid);
    };
    
    // Function to open Guardian with specific chat history
    const openGuardianWithChatHistory = (chatHistoryId: string) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('guardianChatHistoryIdToOpen', chatHistoryId);
            return newParams;
        });
    };
    
    const isLogs = props.alertType == "Log";
    const isKubernetesResourceAlert = props.alertType == "KubernetesResource" || (props.alertType == "MultiMetric" && props.alert.multiMetricAlert && props.alert.multiMetricAlert!.filters.metricSpecifiers.length > 0 && props.alert.multiMetricAlert!.filters.metricSpecifiers[0].metricType == "kubernetes_resource");
    const isKubernetesAlert = props.alertType == "Kubernetes";
    const isMetricAlert = props.alertType == "Metric" || (props.alertType == "MultiMetric" && props.alert.multiMetricAlert && props.alert.multiMetricAlert!.filters.metricSpecifiers.length > 0 && props.alert.multiMetricAlert!.filters.metricSpecifiers[0].metricType == "metric");
    const isTraceAlert = props.alertType == "Trace" || (props.alertType == "MultiMetric" && props.alert.multiMetricAlert && props.alert.multiMetricAlert!.filters.metricSpecifiers.length > 0 && props.alert.multiMetricAlert!.filters.metricSpecifiers[0].metricType == "trace");
    const isLogMetricAlert = props.alertType == "MultiMetric" && props.alert.multiMetricAlert && props.alert.multiMetricAlert!.filters.metricSpecifiers.length > 0 && props.alert.multiMetricAlert!.filters.metricSpecifiers[0].metricType == "logs";
    const isMetoroQlAlert = props.alertType == "MultiMetric" && props.alert.multiMetricAlert && props.alert.multiMetricAlert!.filters.metoroQlQuery != undefined;
    const isMetricAttributePropsSet = props.alertFires.find((alertFire) => {
        if (alertFire.properties && alertFire.properties.metricAttributes) {
            return new Map(Object.entries(alertFire.properties.metricAttributes)).size > 0
        } else {
            return false
        }
    }) != undefined;

    const columns = useMemo<ColumnDef<AlertFire>[]>(() => {
        const baseColumns: ColumnDef<AlertFire>[] = [
            {
                id: "startTime",
                header: "Start",
                accessorFn: (row) => hdate.prettyPrint(new Date(row.startTime * 1000), {showTime: true}),
            },
            {
                id: "endTime",
                header: "End",
                accessorFn: (row) => row.endTime === null ? "Ongoing" : hdate.prettyPrint(new Date(row.endTime * 1000), {showTime: true}),
            }
        ];

        if (isLogs || isKubernetesAlert) {
            baseColumns.push(
                {
                    id: "cluster",
                    header: "Cluster",
                    accessorKey: "kubernetesEnvironment",
                },
                {
                    id: "service",
                    header: "Service",
                    accessorFn: (row) => row.kubernetesService.split("/").pop(),
                }
            );
        }

        if ((isMetricAlert || isTraceAlert || isKubernetesResourceAlert || isLogMetricAlert || isMetoroQlAlert) && isMetricAttributePropsSet) {
            baseColumns.push({
                id: "attributes",
                header: "Attributes",
                accessorFn: (row) => {
                    const metricAttributes = [];
                    const metricAttributeMap = row.properties?.metricAttributes ? new Map(Object.entries(row.properties.metricAttributes)) : new Map();
                    for (let [key, value] of metricAttributeMap.entries()) {
                        metricAttributes.push(`${key}: ${value}`);
                    }
                    return metricAttributes.join(", ");
                },
            });
        }

        if (isMetricAlert || isTraceAlert || isKubernetesResourceAlert || isLogMetricAlert || isMetoroQlAlert) {
            baseColumns.push({
                id: "message",
                header: "Alert Message",
                accessorKey: "message",
            });
        }

        // Add Guardian column if feature flag is enabled and alert has Guardian destination
        if (isGuardianAlertDestinationEnabled && hasGuardianDestination) {
            baseColumns.push({
                id: "guardian",
                header: "Guardian",
                cell: ({ row }) => {
                    const alertFire = row.original;
                    const chatHistory = getChatHistoryForAlertFire(alertFire.uuid);
                    
                    if (chatHistory) {
                        return (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openGuardianWithChatHistory(chatHistory.id)}
                                className="flex items-center gap-2"
                                title={`Open Guardian investigation for ${chatHistory.title}`}
                            >
                                <Bot className="h-4 w-4" />
                                Investigation
                            </Button>
                        );
                    }
                    
                    return (
                        <span className="text-textdark text-sm">No investigation</span>
                    );
                },
            });
        }

        return baseColumns;
    }, [isLogs, isKubernetesAlert, isMetricAlert, isTraceAlert, isKubernetesResourceAlert, isLogMetricAlert, isMetricAttributePropsSet, isGuardianAlertDestinationEnabled, hasGuardianDestination, chatHistories]);

    return (
        <div className="min-w-0 bg-backgroundmedium min-h-0 w-full flex flex-col grow shrink">
            <DataTable
                columns={columns}
                data={props.alertFires}
                filters={[]}
                showPagination={true}
                removeBorder={false}
                tableTitle="Alert Firing History"
                emptyStateMessage={
                    <div className="flex items-center justify-center gap-2 text-textdark">
                        <span>No alerts fired in the selected time period</span>
                        <SmileIcon className="h-5 w-5"/>
                    </div>
                }
            />
        </div>
    );
}
